import * as React from 'react';
import moment from 'moment';
import {
  useState,
  useEffect,
  useRef,
  useContext,
} from 'react';
import useHabitat from '../../../hooks/useHabitat';
import Content from '../Content';
import { Discipline, SanctionSuspensionItem } from '../../../core/typings';
import LocalizationContext from '../../../contexts/Localizations';
import './styles.scss';

interface AccordionPanelProps {
  index: number;
  activeTab: boolean;
  toggleTab: Function;
  label: string | JSX.Element;
  content?: string | JSX.Element | JSX.Element[];
  item?: SanctionSuspensionItem;
  themeColor: string;
  discipline?: string;
  isAsync?: boolean;
  isResults?: boolean;
  isSuspension?: boolean;
}

const AccordionPanel = ({
  index,
  activeTab,
  toggleTab,
  label,
  content,
  item,
  themeColor,
  discipline,
  isAsync,
  isResults,
  isSuspension,
}: AccordionPanelProps) => {
  const [height, setHeight] = useState<string>('0px');
  const [asyncContentLoaded, setAsyncContentLoaded] = useState<boolean>(false);
  const { translations } = useContext(LocalizationContext);
  const wrapper = useRef<HTMLDivElement>(null);

  const calculatePanelHeight = () => {
    const panelElement = wrapper.current as HTMLDivElement;

    if (activeTab) {
      panelElement.ontransitionend = () => {
        setHeight('auto');
      };

      setHeight(`${panelElement.scrollHeight}px`);
    } else {
      panelElement.ontransitionend = null;
      setHeight(`${panelElement.scrollHeight}px`);
      requestAnimationFrame(() => {
        setHeight('0px');
      });
    }
  };

  const handleLoadedContent = () => {
    setAsyncContentLoaded(true);
    calculatePanelHeight();
  };

  useEffect(() => {
    calculatePanelHeight();
  }, [activeTab]);

  return (
    <div className={`panel panel--${themeColor} ${isResults ? 'panel--results' : ''} ${isResults && activeTab ? 'panel--results__open' : ''}`}>
      <button
        type="button"
        className={`panel__label ${
          discipline ? `panel__label--${discipline}` : ''
        }`}
        aria-expanded={activeTab}
        onClick={() => toggleTab(index)}
      >
        {item ? (
          <div className="panel__label__table">
            <span className="panel__label__cell panel__label__cell--name">{label}</span>
            <span className="panel__label__cell panel__label__cell--nationality">{item.nationality}</span>
            <span className="panel__label__cell panel__label__cell--uci">{item.uciId}</span>
            <span className="panel__label__cell panel__label__cell--date">
              {(isSuspension && item.suspensionDate && moment(item.suspensionDate).format('DD/MM/YYYY')) || item.sanctionDate}
            </span>
          </div>
        ) : (
          label
        )}
      </button>
      <div
        ref={wrapper}
        className="panel__inner"
        aria-hidden={!activeTab}
        style={{
          height: `${height}`,
        }}
      >
        {content && typeof content === 'string' && (
          <div className="panel__content">
            <Content content={content} containerTheme={themeColor} />
          </div>
        )}

        {item && (
          <div className="panel__content panel__item">
            <dl className="item-details">
              {item.individualType && (
                <>
                  <dt>{translations.sanctions.individualTypeLabel}</dt>
                  <dd>{item.individualType}</dd>
                </>
              )}

              {item.adrv && (
                <>
                  <dt>{translations.sanctions.adrvLabel}</dt>
                  <dd>{item.adrv}</dd>
                </>
              )}

              {(item.violationDate || item.violationDateOverride) && (
                <>
                  <dt>{translations.sanctions.violationDateLabel}</dt>
                  <dd>{item.violationDateOverride || moment(item.violationDate).format('DD/MM/YYYY')}</dd>
                </>
              )}

              {(item.lifetimeBan || (item.ineligibilityStartDate && item.ineligibilityEndDate)) && (
                <>
                  <dt>{translations.sanctions.ineligibilityPeriodLabel}</dt>
                  <dd>
                    {item.lifetimeBan
                      ? translations.sanctions.lifeBanLabel
                      : `${moment(item.ineligibilityStartDate).format('DD/MM/YYYY')} – ${moment(item.ineligibilityEndDate).format('DD/MM/YYYY')}`}
                  </dd>
                </>
              )}

              {(item.disqualificationPeriodOverride
                || (item.disqualificationStartDate && item.disqualificationEndDate)) && (
                <>
                  <dt>{translations.sanctions.disqualificationPeriodLabel}</dt>
                  <dd>
                    {item.disqualificationPeriodOverride
                      ? item.disqualificationPeriodOverride
                      : `${moment(item.disqualificationStartDate).format('DD/MM/YYYY')} – ${moment(item.disqualificationEndDate).format('DD/MM/YYYY')}`}
                  </dd>
                </>
              )}

              {[item.judgementLink, ...(item.additionalJudgementLinks || [])]
                .filter(Boolean).length > 0 && (
                <>
                  <dt>{translations.sanctions.judgementLabel}</dt>
                  <dd>
                    <div className="panel__additional-links">
                      {[item.judgementLink, ...(item.additionalJudgementLinks || [])]
                        .filter(Boolean)
                        .map((link) => (
                          <a href={link?.url} target="_blank" rel="noopener noreferrer">
                            {link?.title || 'View Judgement'}
                          </a>
                        ))}
                    </div>
                  </dd>
                </>
              )}

              {item.pressReleaseLink && (
                <>
                  <dt>{translations.sanctions.pressReleaseLabel}</dt>
                  <dd>
                    <div className="panel__additional-links">
                      {[item.pressReleaseLink, ...(item.additionalPressReleaseLinks || [])]
                        .filter(Boolean)
                        .map((link) => (
                          <a href={link?.url} target="_blank" rel="noopener noreferrer">
                            {link?.title || 'View Press Release'}
                          </a>
                        ))}
                    </div>
                  </dd>
                </>
              )}
            </dl>
          </div>
        )}

        {!isAsync && React.isValidElement(content) && content}

        {isAsync && (activeTab || asyncContentLoaded) && React.isValidElement(content) && (
          <content.type
            {...(typeof content.props === 'object' ? content.props : {})}
            onLoad={handleLoadedContent}
            onBeforeLoad={calculatePanelHeight}
          />
        )}
      </div>
    </div>
  );
};

export interface AccordionPanelType {
  label: string | JSX.Element;
  content?: string | JSX.Element | JSX.Element[];
  item?: SanctionSuspensionItem;
}

interface AccordionProps {
  panels?: AccordionPanelType[];
  themeColor: string;
  discipline?: Discipline;
  isResults?: boolean;
  isSanctionSuspension?: boolean;
  useSuspensionTitle?: boolean;
}

interface PanelsState {
  label: string | JSX.Element;
  content?: string | JSX.Element | JSX.Element[];
  activeTab: boolean;
}

const Accordion = ({
  panels,
  themeColor,
  discipline,
  isResults,
  isSanctionSuspension,
  useSuspensionTitle,
}: AccordionProps) => {
  const [panelsInState, setPanelsInState] = useState<PanelsState[]>([]);
  const { translations } = useContext(LocalizationContext);

  useHabitat();

  const toggleTab = (index: number) => {
    const current: PanelsState[] = [...panelsInState];

    current[index].activeTab = !panelsInState[index].activeTab;
    setPanelsInState(current);
  };

  useEffect(() => {
    if (panels) {
      const mappedPanels: PanelsState[] = panels.map((panel) => ({
        ...panel,
        activeTab: false,
      }));

      setPanelsInState(mappedPanels);
    }
  }, []);

  return (
    <div className="accordion">
      {isSanctionSuspension && (
        <div className="accordion__header">
          <div className="accordion__header__cell accordion__header__cell--name">{translations.sanctions.nameLabel}</div>
          <div className="accordion__header__cell accordion__header__cell--nationality">
            <span className="accordion__header__cell__full">{translations.sanctions.nationalityLabel}</span>
            <span className="accordion__header__cell__short">{translations.sanctions.nationalityLabelShort}</span>
          </div>
          <div className="accordion__header__cell accordion__header__cell--uci">
            <span className="accordion__header__cell__full">{translations.sanctions.uciIdLabel}</span>
            <span className="accordion__header__cell__short">{translations.sanctions.uciIdLabelShort}</span>
          </div>
          <div className="accordion__header__cell accordion__header__cell--date">
            <span className="accordion__header__cell__full">
              {useSuspensionTitle
                ? translations.sanctions.suspensionLabel
                : translations.sanctions.sanctionLabel}
            </span>
            <span className="accordion__header__cell__short">{translations.sanctions.sanctionLabelShort}</span>
          </div>
        </div>
      )}
      {panelsInState && panelsInState.map((panel, index) => (
        <AccordionPanel
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          toggleTab={toggleTab}
          index={index}
          themeColor={themeColor}
          discipline={discipline}
          isResults={isResults}
          isSuspension={useSuspensionTitle}
          {...panel}
        />
      ))}
    </div>
  );
};

export default Accordion;
